import { formatFieldKey } from "./cmsLabels";
import { getFirstErrorSection } from "./validation";

/** DOM id for scroll/focus targets (`en.hero.headline.prefix` → `cms-field-en-hero-headline-prefix`). */
export function fieldAnchorId(errorKey) {
  return `cms-field-${String(errorKey).replace(/[^a-zA-Z0-9]+/g, "-")}`;
}

/** Human label for toasts and summaries: "English · Hero · Headline · Prefix". */
export function formatValidationErrorLabel(errorKey) {
  const parts = String(errorKey).split(".");
  let langLabel = null;
  if (parts[0] === "en" || parts[0] === "es") {
    langLabel = parts[0] === "en" ? "English" : "Spanish";
    parts.shift();
  }
  const pathLabel = formatFieldKey(parts.join("."));
  return [langLabel, pathLabel].filter(Boolean).join(" · ") || errorKey;
}

export function getValidationErrorKeys(errors) {
  return Object.keys(errors || {}).filter((key) => key !== "_form");
}

export function scrollToFirstValidationError(errors, options = {}) {
  const keys = getValidationErrorKeys(errors);
  if (!keys.length) return null;

  const firstKey = keys[0];
  const normalized = firstKey.replace(/^(en|es)\./, "");

  if (options.sectionPrefixMap) {
    const sectionId = getFirstErrorSection(errors, options.sectionPrefixMap);
    if (sectionId) {
      const sectionEl = document.getElementById(sectionId);
      if (sectionEl) {
        sectionEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
      if (typeof options.onSectionChange === "function") {
        options.onSectionChange(sectionId);
      }
    }
  } else if (typeof options.onSectionChange === "function") {
    const sectionId = options.onSectionChange(normalized, firstKey);
    if (sectionId) {
      const sectionEl = document.getElementById(sectionId);
      if (sectionEl) {
        sectionEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }

  const focusField = () => {
    const target =
      document.getElementById(fieldAnchorId(firstKey)) ||
      document.querySelector(`[data-cms-field="${firstKey}"]`) ||
      document.querySelector(`[data-cms-field="${firstKey}.src"]`) ||
      document.querySelector(`[data-cms-field="${firstKey}.alt"]`);

    if (!target) return;

    target.scrollIntoView({ behavior: "smooth", block: "nearest" });
    target.classList.add("cms-field-anchor--focus");

    const focusable = target.querySelector(
      "input.form-control:not([type=file]), textarea.form-control, select.form-control"
    );
    if (focusable && typeof focusable.focus === "function") {
      focusable.focus({ preventScroll: true });
    }

    window.setTimeout(() => {
      target.classList.remove("cms-field-anchor--focus");
    }, 2600);
  };

  // Allow collapsible sections to expand before scrolling to the field anchor.
  window.requestAnimationFrame(() => {
    window.setTimeout(focusField, 120);
  });

  return firstKey;
}

/** Shared save validation: set errors, scroll to first field, optional section tab. */
export function applyCmsValidationFailure({
  validationErrors,
  setErrors,
  sectionPrefixMap,
  onSectionChange,
  toastMessage = "Fix validation errors before saving.",
  toastFn,
}) {
  setErrors(validationErrors);
  scrollToFirstValidationError(validationErrors, {
    sectionPrefixMap,
    onSectionChange,
  });
  if (toastFn) {
    const keys = getValidationErrorKeys(validationErrors);
    const detail = keys.length ? formatValidationErrorLabel(keys[0]) : null;
    toastFn(detail ? `${toastMessage} (${detail})` : toastMessage);
  }
}
